import { useRef, useState } from "react";
import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import {
  Stack,
  NumberInput,
  Select,
  Button,
  Text,
  rem,
  ThemeIcon,
  Group,
  Card,
  Divider,
  Tabs,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconTrophy,
  IconCheck,
  IconX,
  IconMinus,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { isNumber } from "lodash";
import {
  MatchScoreStatus,
  Player,
  TournamentTeam,
  TournamentTeamMemberDto,
  UpdateMatchScoreInput,
} from "../../../../../gql/graphql";

const useStyles = createStyles((theme) => ({
  container: {
    minHeight: rem(280),
  },
  teamCard: {
    backgroundColor: theme.colors.dark[6],
    border: `1px solid ${theme.colors.dark[4]}`,
    padding: theme.spacing.md,
    borderRadius: theme.radius.md,
    marginBottom: theme.spacing.md,
  },
  teamName: {
    fontSize: theme.fontSizes.lg,
    fontWeight: 600,
    color: theme.colors.dark[0],
  },
  inputSection: {
    marginTop: theme.spacing.md,
  },
  statusBadge: {
    padding: `${rem(4)} ${rem(12)}`,
    borderRadius: theme.radius.sm,
    fontWeight: 600,
    fontSize: theme.fontSizes.xs,
    textTransform: "uppercase",
  },
  updateButton: {
    fontWeight: 600,
    marginTop: theme.spacing.lg,
  },
  scoreInput: {
    "& input": {
      fontSize: theme.fontSizes.xl,
      fontWeight: 700,
      textAlign: "center",
    },
  },
}));

type TeamWithMembers = Omit<TournamentTeam, "members"> & {
  members: (TournamentTeamMemberDto & { player: Player })[];
};

interface ScoreData {
  teamId: string;
  status: MatchScoreStatus;
  score: number;
  matchId: string;
}

export const createDefaultScore = (
  teamId: string,
  matchId: string,
): ScoreData => ({
  teamId,
  status: MatchScoreStatus.Waiting,
  score: 0,
  matchId,
});

interface UpdateScoreModalProps {
  scores: ScoreData[];
  team: TeamWithMembers;
  onSuccess?: () => void;
}

const UPDATE_MATCH_SCORES_MUTATION = gql`
  mutation ($matchId: ID!, $input: [UpdateMatchScoreInput!]!) {
    updateMatchScores(matchId: $matchId, input: $input) {
      id
    }
  }
`;

const statusConfig: Record<
  MatchScoreStatus,
  { color: string; icon: React.ReactNode; label: string }
> = {
  [MatchScoreStatus.Winner]: {
    color: "green",
    icon: <IconTrophy size={14} />,
    label: "Winner",
  },
  [MatchScoreStatus.Loser]: {
    color: "rose",
    icon: <IconX size={14} />,
    label: "Loser",
  },
  [MatchScoreStatus.Forfeit]: {
    color: "rose",
    icon: <IconAlertTriangle size={14} />,
    label: "Forfeit",
  },
  [MatchScoreStatus.Waiting]: {
    color: "dark",
    icon: <IconMinus size={14} />,
    label: "Waiting",
  },
  [MatchScoreStatus.Draw]: {
    color: "gray",
    icon: <IconMinus size={14} />,
    label: "Draw",
  },
};

export const UpdateScoreModal = ({
  scores,
  team,
  onSuccess,
}: UpdateScoreModalProps) => {
  const { classes } = useStyles();
  const [loading, setLoading] = useState(false);

  const toEntry = (s: ScoreData) => ({
    matchId: s.matchId,
    score: s.score,
    status: s.status,
  });

  const originalEntries = useRef(scores.map(toEntry)).current;

  const [matchEntries, setMatchEntries] = useState(
    scores.filter((s) => s.status === MatchScoreStatus.Waiting).map(toEntry),
  );

  const hasMultipleMatches = matchEntries.length > 1;

  const [updateMatchScores] = useMutation<
    { updateMatchScores: { id: string } },
    { matchId: string; input: UpdateMatchScoreInput[] }
  >(UPDATE_MATCH_SCORES_MUTATION);

  const updateEntry = (
    index: number,
    field: "score" | "status",
    value: number | MatchScoreStatus,
  ) => {
    setMatchEntries((prev) =>
      prev.map((entry, i) =>
        i === index ? { ...entry, [field]: value } : entry,
      ),
    );
  };

  const handleUpdate = async () => {
    const changedEntries = matchEntries.filter((entry) => {
      const original = originalEntries.find((o) => o.matchId === entry.matchId);
      return (
        !original ||
        original.score !== entry.score ||
        original.status !== entry.status
      );
    });

    if (changedEntries.length === 0) {
      modals.closeAll();
      return;
    }

    setLoading(true);
    try {
      const results = await Promise.all(
        changedEntries.map((entry) =>
          updateMatchScores({
            variables: {
              matchId: entry.matchId,
              input: [
                {
                  teamId: team.id,
                  forcedScoreValue: entry.score,
                  status: entry.status,
                },
              ],
            },
          }),
        ),
      );

      const firstError = results.find((r) => r?.error);
      if (firstError?.error) {
        throw new Error(firstError.error.message);
      }

      const endedMatchIds = changedEntries
        .filter((e) => e.status !== MatchScoreStatus.Waiting)
        .map((e) => e.matchId);

      const remaining = matchEntries.filter(
        (e) => !endedMatchIds.includes(e.matchId),
      );

      notifications.show({
        title: "Score Updated",
        message: `Updated score for ${team.name}`,
        color: "green",
        icon: <IconCheck size={16} />,
      });

      onSuccess?.();

      if (remaining.length === 0) {
        modals.closeAll();
      } else {
        setMatchEntries(remaining);
      }
    } catch (error) {
      notifications.show({
        title: "Update Failed",
        message:
          error instanceof Error ? error.message : "Failed to update score",
        color: "rose",
      });
    } finally {
      setLoading(false);
    }
  };

  const renderMatchFields = (index: number) => {
    const entry = matchEntries[index];

    return (
      <Stack key={entry.matchId}>
        <div className={classes.inputSection}>
          <NumberInput
            className={classes.scoreInput}
            label="Score"
            description="Enter the team's score for this match"
            value={entry.score}
            min={0}
            onChange={(value) => {
              updateEntry(index, "score", isNumber(value) ? value : 0);
            }}
            size="lg"
          />
        </div>

        <Select
          label="Match Status"
          description="Set the team's status in this match"
          data={Object.values(MatchScoreStatus).map((s) => ({
            value: s,
            label: statusConfig[s].label,
          }))}
          value={entry.status}
          onChange={(value) => {
            if (value) updateEntry(index, "status", value as MatchScoreStatus);
          }}
        />
      </Stack>
    );
  };

  if (matchEntries.length === 0) {
    return (
      <Stack className={classes.container}>
        <Group>
          <ThemeIcon size="lg" radius="md" variant="light" color="cyan">
            <IconTrophy size={20} />
          </ThemeIcon>
          <Text fw={600}>All matches have ended</Text>
        </Group>
        <Button onClick={() => modals.closeAll()} fullWidth>
          Close
        </Button>
      </Stack>
    );
  }

  const currentStatusConfig = statusConfig[matchEntries[0].status];

  return (
    <Stack className={classes.container}>
      <Group>
        <ThemeIcon size="lg" radius="md" variant="light" color="cyan">
          <IconTrophy size={20} />
        </ThemeIcon>
        <div>
          <Text fw={600}>Update Match Score</Text>
          <Text size="xs" c="dimmed">
            {hasMultipleMatches
              ? `Modify scores and statuses across ${matchEntries.length} matches`
              : "Modify score and status for this team"}
          </Text>
        </div>
      </Group>

      <Card className={classes.teamCard}>
        <Group justify="space-between">
          <div>
            <Text className={classes.teamName}>{team.name}</Text>
            <Text size="xs" c="dimmed">
              {team.members.map((m) => m.player.username).join(", ")}
            </Text>
          </div>
          <Group
            gap={6}
            className={classes.statusBadge}
            style={{
              backgroundColor: `var(--mantine-color-${currentStatusConfig.color}-9)`,
              color: `var(--mantine-color-${currentStatusConfig.color}-4)`,
            }}
          >
            {currentStatusConfig.icon}
            {currentStatusConfig.label}
          </Group>
        </Group>
      </Card>

      <Divider />

      {hasMultipleMatches ? (
        <Tabs defaultValue="0">
          <Tabs.List>
            {matchEntries.map((_, index) => (
              <Tabs.Tab key={index} value={String(index)}>
                Match {index + 1}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {matchEntries.map((_, index) => (
            <Tabs.Panel key={index} value={String(index)} pt="md">
              {renderMatchFields(index)}
            </Tabs.Panel>
          ))}
        </Tabs>
      ) : (
        renderMatchFields(0)
      )}

      <Button
        className={classes.updateButton}
        loading={loading}
        onClick={handleUpdate}
        leftSection={<IconCheck size={16} />}
        fullWidth
      >
        {hasMultipleMatches ? "Update All Matches" : "Update Score"}
      </Button>
    </Stack>
  );
};

export const openUpdateScoreModal = (props: UpdateScoreModalProps) => {
  modals.open({
    title: null,
    centered: true,
    size: "md",
    children: <UpdateScoreModal {...props} />,
  });
};
